import React, { useEffect, useMemo } from 'react';
import { graphql, type HeadProps } from 'gatsby';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import _ from 'lodash';
import LooxWidgetScript from '../components/Product/LooxWidgetScript';
import SEO from '../components/Storyblok/SEO';
import { RootStateOverride } from '../context/root-state.context';
import { useStoryblokSSG } from '../hooks';
import { dataLayerViewItem } from '../utils/dataLayerEvents.js';
import { PageSEO, SourcedShopifyProduct, StoryblokDataSourceEntry, StoryblokEntry } from '../types';

type ProductQuery = {
  shopifyProduct: SourcedShopifyProduct;
  filteredStoryBlokDataSourceEntries: {
    nodes: StoryblokDataSourceEntry[];
  };
};

type ProductPageContext = {
  story: StoryblokEntry;
  slug: string;
  productType: string;
};

type ProductPageProps = {
  data: ProductQuery;
  pageContext: ProductPageContext;
};

const ProductStarWars = ({ pageContext, data }: ProductPageProps) => {
  const { storyToRender } = useStoryblokSSG(pageContext.story, { slug: pageContext.slug });

  const productColors = useMemo(() => _.get(data, 'filteredStoryBlokDataSourceEntries.nodes', []), [data]);
  const rootStateOverrideValue = useMemo(
    () => ({ shopifyProduct: data.shopifyProduct, productColors }),
    [data.shopifyProduct, productColors],
  );

  // view_item data layer push
  useEffect(() => {
    const transformedShopifyProduct = {
      priceRangeV2: data.shopifyProduct?.priceRangeV2,
      shopifyId: data.shopifyProduct?.shopifyId,
      title: data.shopifyProduct?.title,
    };

    dataLayerViewItem(transformedShopifyProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="h-full" id="bodyContent">
        {storyToRender?.content && (
          <RootStateOverride value={rootStateOverrideValue}>
            <StoryblokComponent blok={storyToRender.content} />
          </RootStateOverride>
        )}
      </div>

      <LooxWidgetScript />
    </>
  );
};

export default ProductStarWars;

export function Head({ location, data, pageContext }: HeadProps<ProductQuery, ProductPageContext>) {
  if (!pageContext.story) return null;

  const { shopifyProduct } = data;
  const content = JSON.parse(pageContext.story.content) as PageSEO;
  const metaData = {
    title: content.metaDataTitle,
    description: content.metaDataDescription,
    image: content.metaDataImage?.filename,
    imageTwitter: content.metaDataImageTwitter?.filename,
    imageAltText: content?.metaDataImageAltText,
    location,
    fbDomainVerification: content.metaDataFbDomainVerification,

    productName: shopifyProduct?.title,
  };

  // @ts-expect-error SEO component is not typed
  return <SEO {...metaData} />;
}

export const query = graphql`
  query ProductQuery($productType: String) {
    shopifyProduct(handle: { eq: $productType }) {
      title
      handle
      storefrontId
      shopifyId
      description
      totalInventory
      productType
      priceRangeV2 {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      variants {
        availableForSale
        storefrontId
        title
        price
        selectedOptions {
          name
          value
        }
      }
      options {
        name
        values
      }
    }
    filteredStoryBlokDataSourceEntries: allStoryblokDatasourceEntry(filter: { data_source: { eq: "product-color" } }) {
      nodes {
        id
        name
        value
      }
    }
  }
`;
